<template>
  <div
    ref="main"
    class="nosto-filter"
    :class="{ 'is-sticky': isSticky }"
  >
    <div class="nosto-filter__inner">
      <button
        class="
            nosto-filter__filter-button
            text-heading-3xs
            text-heading-xs-desktop
          "
        type="button"
        @click="handleOpenDrawer"
      >
        <icon-filter />
        <span v-text="$string('collection.filter')" />
      </button>

      <div class="nosto-filter__sort-by">
        <div
          class="
              nosto-filter__sort-by-button
              text-heading-3xs
              text-heading-xs-desktop
            "
        >
          <icon-chevron-down />

          <span
            class="nosto-filter__sort-by-label"
            v-text="sortByLabel"
          />
        </div>

        <select
          id="sort-by"
          ref="sortBy"
          class="nosto-filter__sort-by-select"
          name="sort-by"
          :title="$string('collection.sort_by')"
          @change="handleSortByChange"
        >
          <option
            v-for="sort of getActiveOptions"
            :id="sort.id"
            :key="sort.id"
            :value="sort.id"
            :selected="sortOption.id === sort.id"
            js-nosto-filter="sortByOption"
            v-text="sort.label"
          />
        </select>
      </div>

      <div
        class="nosto-filter__facets-wrap"
      >
        <div
          class="nosto-filter__item-count text-label-s"
          v-html="itemCountLabel"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Nosto filter (main-collection)
 * -----------------------------------------------------------------------------
 * Collection and search filters.
 *
 * @param {Boolean} nostoReady - nosto is ready.
 * @param {String} choices - Available choices.
 * @param {Object} sortByOptions - Options for sorting.
 * @param {Object} total - Total number of products/collections/pages.
 *
 */
import { mapActions, mapState } from 'vuex'

import IconChevronDown from '~icons/direction/chevron/down.svg'
import IconFilter from '~icons/misc/filter.svg'

export default {
  name: 'NostoFilter',

  components: {
    IconChevronDown,
    IconFilter,
  },

  props: {
    nostoReady: {
      type: Boolean,
      default: false,
    },
    choices: {
      type: String,
      default: '',
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    total: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
    'clear-choices': () => true,
    'facets-ready': () => true,
  },

  data() {
    return {
      choicesArray: [],
      dataChoices: '',
      hasNonPageValue: false,
      sortOption: {
        id: '',
        label: '',
      },
      isSticky: false,
      mainElementScrollTop: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      scroll: (state) => state.index.scroll,
    }),

    /**
     * Compute active options by filtering 'sort by' props based on those
     * enabled in section settings.
     * @returns {Object}
     */
    getActiveOptions() {
      return Object.values(this.sortByOptions).filter((object) => {
        return object.value === true
      })
    },

    /**
     * Compute item count label.
     * @returns {String}
     */
    itemCountLabel() {
      const count = this.nostoReady
        ? this.total
        : '<span></span>'

      return this.$string('collection.items', {
        pluralise: this.total,
        replace: { count },
      })
    },

    /**
     * Compute sort by label.
     * @returns {String}
     */
    sortByLabel() {
      return this.dataChoices.includes('sort')
        ? `${this.$string('collection.sort_by')} ${this.sortOption.label}`
        : this.$string('collection.sort_by')
    },
  },

  watch: {

    /**
     * Watch choices to update array.
     */
    choices(choices) {
      this.dataChoices = choices
      this.updateChoicesArray(choices)

      /**
       * Reset sort by select value when not sorting.
       */
      if (choices.includes('&sort=')) {
        return
      }

      if (this.$refs.sortBy) {
        this.$refs.sortBy.selectedIndex = 0
      }
    },

    /**
     * Watch scroll to made filter bar sticky.
     */
    scroll() {
      this.handleScroll()
    },
  },

  mounted() {
    this.mainElementScrollTop = this.$refs.main.offsetTop
    this.handleScroll()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Open drawer on filter button click.
     */
    handleOpenDrawer() {
      if (!this.nostoReady) {
        return
      }

      this.openOverlay({
        component: 'nosto-drawer',
        ignoreDismissed: true,
        namespace: 'nostoDrawer',
      })
    },

    /**
     * Receive user input from facets and pass up to main collection.
     * @param {String} data - Formatted update to user choices.
     */
    choicesUpdated(data) {
      this.$emit('choices-updated', data)
    },

    /**
     * Notify when 'clear all' button is clicked.
     */
    clearChoices() {
      this.$emit('clear-choices')
      this.hasNonPageValue = false

      this.sortOption = {
        id: '',
        label: '',
      }
    },

    /**
     * Update choices array passed to active values component on choices change.
     * @param {String} data - Choices string to split into an array.
     */
    updateChoicesArray(data) {
      this.choicesArray = data.split('&').filter((choice) => {
        return choice !== ''
      })

      if (!this.choicesArray.length) {
        this.hasNonPageValue = false

        this.sortOption = {
          id: '',
          label: '',
        }

        return
      }

      this.choicesArray.forEach((choice) => {
        if (!choice.includes('page')) {
          this.hasNonPageValue = true
        }

        if (choice.includes('sort')) {
          const value = choice.split('=')[1]

          const option = Object.values(this.sortByOptions).find((object) => {
            return object.id === value
          })

          const { id, label } = option

          this.sortOption = {
            id,
            label,
          }
        }
      })
    },

    /**
     * Update sort options on select change.
     * @param {Event} event - Change event.
     */
    handleSortByChange(event) {
      if (!this.nostoReady) {
        return
      }

      const nativeOptions = document.querySelectorAll('[js-nosto-filter="sortByOption"]')
      let matchValue = event.currentTarget.value
      let action = 'add'

      if (event.currentTarget.classList.contains('nosto-filter__sort-by-desktop--option')) {
        matchValue = event.currentTarget.id

        if (event.currentTarget.classList.contains(this.$class('active'))) {
          event.currentTarget.classList.remove(this.$class('active'))
          action = 'remove'

          nativeOptions.forEach((option) => {
            option.removeAttribute('selected')
          })

        } else {
          event.currentTarget.classList.add(this.$class('active'))

          nativeOptions.forEach((option) => {
            if (option.id === matchValue) {
              option.setAttribute('selected', 'selected')

            } else {
              option.removeAttribute('selected')
            }
          })
        }
      }

      const option = Object.values(this.sortByOptions).find((object) => {
        return object.id === matchValue
      })

      const { id, label } = option

      const data = {
        requestParam: `&sort=${id}`,
        action,
      }

      this.$emit('choices-updated', data)

      this.sortOption = {
        id,
        label,
      }
    },

    /**
     * Return empty or full circle icon based on active status of option.
     * @param {String} sortId - Id of sort option.
     */
    getSortIcon(sortId) {
      if (this.dataChoices.includes(sortId)) {
        return 'icon-circle-full'
      }

      return 'icon-circle-empty'
    },

    /**
     * Handle scroll for filter bar.
     */
    handleScroll() {
      const mainElement = this.$refs.main
      const headerHeight = document.getElementById('site-header-container').offsetHeight

      if (this.scroll.direction === 'down') {
        this.isSticky = this.scroll.y >= this.mainElementScrollTop
        mainElement.style.top = '-1px'
        mainElement.classList.remove('up')
        mainElement.classList.add('down')

        return
      }

      if (this.scroll.direction === 'up' && this.scroll.y >= (this.mainElementScrollTop - headerHeight)) {
        this.isSticky = true
        mainElement.style.top = `${headerHeight - 1}px`
        mainElement.classList.remove('down')
        mainElement.classList.add('up')
      } else {
        this.isSticky = false
        mainElement.style.top = 0
      }
    },
  },
}
</script>

<style lang="scss">
@import './nosto-filter';
</style>
